import cx from 'classnames';
import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {EditorIcon} from '~/common/FabricInvitation/EventCreateDesign/components/EditorIcons/EditorIcon';
import {selectBackgroundColor} from '~/common/fabric/reducers/fabricDesign/selectors';
import {AssetIconType} from '~/common/types/assets_icons';

import './EditorIcons.sass';

interface EditorIconsProps {
  icons?: AssetIconType[];
  onClick?: (icon: AssetIconType, id?: string | number | null) => void;
  selectedIcon?: AssetIconType;
  height?: number;
  width?: number;
  className?: string;
  hideAnimatedIndicator?: boolean;
  flipEnvelope?: (isFrontSide: boolean) => void;
  showEnvelopeFront?: boolean | null;
  circle?: boolean;
  noPadding?: boolean;
  outline?: boolean;
}

const getIconId = (icon?: AssetIconType, backgroundColor?: string): string => {
  let iconId = '';

  if (typeof icon === 'object') {
    iconId = icon.id || 'none';
  } else {
    iconId = icon || 'none';
  }

  // only when there is no background color should none be selected
  if (iconId === 'none' && backgroundColor) {
    return '';
  }

  return iconId;
};

export const EditorIcons = ({
  icons = [],
  onClick,
  selectedIcon,
  height,
  width,
  className,
  hideAnimatedIndicator,
  flipEnvelope,
  showEnvelopeFront,
  circle,
  noPadding = false,
  outline = false,
}: EditorIconsProps) => {
  const backgroundColor = useSelector(selectBackgroundColor);
  const onClickHandler = useCallback(
    (icon: AssetIconType, id: string | number) => {
      if (onClick) {
        onClick(icon, id);
      }
    },
    [onClick]
  );

  const iconsMemo = useMemo(
    () =>
      icons?.map((icon: AssetIconType) => {
        const iconId = getIconId(icon, backgroundColor);
        const selectedIconId = getIconId(selectedIcon, backgroundColor);

        return (
          <EditorIcon
            key={iconId}
            icon={icon}
            onClick={onClickHandler}
            isActive={Boolean(selectedIconId && iconId && selectedIconId === iconId)}
            height={height}
            width={width}
            hideAnimatedIndicator={hideAnimatedIndicator}
            flipEnvelope={flipEnvelope}
            showEnvelopeFront={showEnvelopeFront}
            circle={circle}
            outline={outline}
          />
        );
      }),
    [
      icons,
      onClickHandler,
      selectedIcon,
      height,
      width,
      hideAnimatedIndicator,
      flipEnvelope,
      showEnvelopeFront,
      circle,
      outline,
      backgroundColor,
    ]
  );

  return (
    <div className={cx('editor-icons', className, {'editor-icons--no-padding': noPadding})}>
      {iconsMemo}
    </div>
  );
};
